import type { CSSProperties, RefObject } from 'react'
import type { LeagueMember } from '../../types'
import { avatarPresetForId } from '../../avatarPresets'
import { LIST_STAGGER_ROW_COUNT } from '../../sharedElementTransition'
import { AvatarGlyph } from '../ProfileAvatar'
import DeltaBadge from './DeltaBadge'

// Compact avatar for a dense list row — texture, not identification. Falls back to a deterministic
// preset derived from the member id when they never chose one.
export function RowAvatar({
  member,
  isYou,
}: {
  member: LeagueMember
  isYou: boolean
}) {
  const preset = member.avatarPreset ?? avatarPresetForId(member.id)
  return (
    <span
      className={`league-detail__avatar ${isYou ? 'league-detail__avatar--you' : ''}`}
      aria-hidden="true"
    >
      <AvatarGlyph preset={preset} className="league-detail__avatar-glyph" />
    </span>
  )
}

// Per-member movement delta since the last deadline. The snapshot covers every membership, so
// every row gets a real arrow. A member with no movement (or no baseline yet) renders an empty,
// width-holding cell so the points column stays aligned.
export function RowDelta({ member }: { member: LeagueMember }) {
  const delta = member.movementDelta ?? null
  if (delta) {
    return <DeltaBadge delta={delta} baseClass="league-detail__row-delta" />
  }
  return <span className="league-detail__row-delta" aria-hidden="true" />
}

interface LeagueStandingsProps {
  standings: LeagueMember[]
  onViewProfile: (userId: string, displayName: string) => void
  // The user's own row anchors the sticky bar's duplicate suppression and its jump target.
  // 
  youRowRef: RefObject<HTMLDivElement | null>
}

function LeagueStandings({
  standings,
  onViewProfile,
  youRowRef,
}: LeagueStandingsProps) {
  return (
    <div className="league-detail__members">
      {standings.map((member, index) => {
        const rank = index + 1
        const isYou = Boolean(member.isYou)
        // Per-row reveal delay (§ list row stagger): the first N rows stagger; the rest share the
        // final delay so they surface together rather than trailing off.
        const rowIndexStyle = {
          '--row-index': Math.min(index, LIST_STAGGER_ROW_COUNT - 1),
        } as CSSProperties

        const rowInner = (
          <>
            <span className="league-detail__rank">{rank}</span>
            <RowAvatar member={member} isYou={isYou} />
            <span className="league-detail__row-name">{member.name}</span>
            <RowDelta member={member} />
            <span className="league-detail__points">{member.points}</span>
          </>
        )

        return isYou ? (
          <div
            key={member.id}
            ref={youRowRef}
            className="league-detail__row league-detail__row--you"
            style={rowIndexStyle}
          >
            {rowInner}
          </div>
        ) : (
          <button
            key={member.id}
            type="button"
            className="league-detail__row league-detail__row--pressable"
            style={rowIndexStyle}
            onClick={() => onViewProfile(member.id, member.name)}
          >
            {rowInner}
          </button>
        )
      })}
    </div>
  )
}

export default LeagueStandings
